import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { useCreateLead, useUpdateLead, useLead } from '../hooks/useLeads';
import { LeadForm } from '../components/leads';
import { LeadCreate } from '../types/lead';

const LeadFormPage: React.FC = () => {
    const { leadId } = useParams<{ leadId: string }>();
    const navigate = useNavigate();
    const isEdit = Boolean(leadId);

    // Hooks for API operations
    const { data: lead, isLoading: isLeadLoading, error: leadError } = useLead(leadId);
    const createLead = useCreateLead();
    const updateLead = useUpdateLead();

    const handleBack = () => {
        if (isEdit) {
            navigate(`/admin/leads/${leadId}`);
        } else {
            navigate('/admin/leads');
        }
    };

    const handleSubmit = async (formData: LeadCreate) => {
        try {
            if (isEdit && leadId) {
                await updateLead.mutateAsync({ id: leadId, data: formData });
                navigate(`/admin/leads/${leadId}`);
            } else {
                await createLead.mutateAsync(formData);
                navigate('/admin/leads');
            }
        } catch (error) {
            console.error('Failed to save lead:', error);
            // useLeads hook already handles showing errors via snackbar
        }
    };

    if (isEdit && isLeadLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (isEdit && leadError) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Failed to load lead details. It might have been deleted or you don't have permission.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                    {isEdit ? 'Edit Lead' : 'Create New Lead'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {isEdit
                        ? `Updating details for ${lead?.name || 'lead'}`
                        : 'Enter the details below to add a new sales lead to the system.'}
                </Typography>
            </Box>

            <LeadForm
                initialData={lead}
                onSubmit={handleSubmit}
                isLoading={createLead.isPending || updateLead.isPending}
                isEdit={isEdit}
            />
        </Container>
    );
};

export default LeadFormPage;
